import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios from '../Util/axiosConfig';
import { baseUrl } from '../Contants';
import { AppContext } from './AppContextProvide';
import { setUser as setLocalUser } from '../helpers';
import {
  formatDttm as fmtDttm,
  formatDate as fmtDate,
  setActiveZones,
  subscribeZones,
  getZoneVersion,
  FALLBACK_TIMEZONE,
} from '../Util/timezone';

export const TimezoneContext = createContext();

const readCache = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (e) {
    return fallback;
  }
};

// Resolves the effective display timezone for the signed-in user and exposes
// bound formatters. Resolution order (matches the server intent):
//   user.timezone  ->  instance DEFAULT_TIMEZONE  ->  hardcoded fallback.
// The storage anchor (what stored values are reinterpreted FROM) comes from the
// server's STORAGE_TIMEZONE so history is never misrepresented.
export const TimezoneContextProvider = ({ children }) => {
  const { user, setUser } = useContext(AppContext);
  const [storageZone, setStorageZone] = useState(() =>
    readCache('tzStorage', FALLBACK_TIMEZONE)
  );
  const [defaultZone, setDefaultZone] = useState(() =>
    readCache('tzDefault', FALLBACK_TIMEZONE)
  );

  const isLoggedIn =
    user && ['a', 'rw', 'ro'].includes(user.access_type_cd);

  useEffect(() => {
    let active = true;
    async function loadSettings() {
      try {
        const result = await axios.get(`${baseUrl}/api/settings/timezone`);
        if (!active) return;
        if (result?.data?.storageTimezone) {
          setStorageZone(result.data.storageTimezone);
          try { localStorage.setItem('tzStorage', result.data.storageTimezone); } catch (e) {}
        }
        if (result?.data?.defaultTimezone) {
          setDefaultZone(result.data.defaultTimezone);
          try { localStorage.setItem('tzDefault', result.data.defaultTimezone); } catch (e) {}
        }
      } catch (e) {
        // Keep fallbacks; never block rendering on a settings fetch failure.
      }
    }
    if (isLoggedIn) loadSettings();
    return () => {
      active = false;
    };
  }, [isLoggedIn]);

  const effectiveZone = user?.timezone || defaultZone || FALLBACK_TIMEZONE;

  // Mirror the resolved zones into the module-level formatter so components that
  // call the plain formatDttm (without this hook) render in the same zone.
  useEffect(() => {
    setActiveZones({ storageZone, effectiveZone });
  }, [storageZone, effectiveZone]);

  const formatDttm = useCallback(
    (value, opts = {}) =>
      fmtDttm(value, { displayZone: effectiveZone, storageZone, ...opts }),
    [effectiveZone, storageZone]
  );

  const formatDate = useCallback((value, opts = {}) => fmtDate(value, opts), []);

  // Persist the current user's own preference. Empty clears it (inherit default).
  const saveUserTimezone = useCallback(
    async (timezone) => {
      await axios.post(`${baseUrl}/api/settings/timezone/user`, {
        timezone: timezone || '',
      });
      const updated = { ...user, timezone: timezone || null };
      setUser(updated);
      setLocalUser(updated);
    },
    [user, setUser]
  );

  // Persist the instance-wide default (server enforces the admin gate).
  const saveDefaultTimezone = useCallback(async (timezone) => {
    await axios.put(`${baseUrl}/api/settings/timezone/default`, { timezone });
    setDefaultZone(timezone);
    try { localStorage.setItem('tzDefault', timezone); } catch (e) {}
  }, []);

  return (
    <TimezoneContext.Provider
      value={{
        storageZone,
        defaultZone,
        effectiveZone,
        userTimezone: user?.timezone || null,
        formatDttm,
        formatDate,
        saveUserTimezone,
        saveDefaultTimezone,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => useContext(TimezoneContext);

// Subscribe a component to active-zone changes so it re-renders when the user or
// admin changes the timezone — even if it formats via the plain module functions
// rather than useTimezone(). Returns the current version (usable as a React key
// to force a subtree to re-read the zones).
export const useZoneVersion = () => {
  const [version, setVersion] = useState(getZoneVersion());
  useEffect(() => subscribeZones(setVersion), []);
  return version;
};
